import React, { useState, useRef } from 'react';
import { X, Plus, Trash2, Eye, EyeOff, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { CreateGameProviderRequest } from '../../../types';
import { useCreateGameProvider } from '../../../hooks/useApi';
import { copyToClipboard, cn } from '../../../utils';
import toast from 'react-hot-toast';
import {
  GAME_PROVIDER_CURRENCY_OPTIONS,
  GAME_PROVIDER_CURRENCY_DATALIST_ID,
  gameProviderNativeSelectClass,
  gameProviderTextareaClass,
} from './gameProviderFormShared';

interface CreateGameProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FORM_ID = 'create-game-provider-form';
const CURRENCY_LIST_ID = `${GAME_PROVIDER_CURRENCY_DATALIST_ID}-create`;

const CreateGameProviderModal: React.FC<CreateGameProviderModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState<CreateGameProviderRequest>({
    code: '',
    name: '',
    currency: 'USD',
    minDeposit: undefined,
    maxDeposit: undefined,
    status: 'active',
    displayOrder: undefined,
    clientTag: '',
    config: {},
    metadata: {},
    adminLink: '',
    gameUrl: '',
    adminAccountLogin: {
      username: '',
      password: '',
      note: '',
    },
    adminInfo: {},
  });

  const [adminInfoEntries, setAdminInfoEntries] = useState<Array<{ key: string; value: string }>>([]);
  const [showPassword, setShowPassword] = useState(false);
  const adminInfoKeyInputRef = useRef<HTMLInputElement>(null);

  const createProvider = useCreateGameProvider();

  if (!isOpen) return null;

  const handleAddAdminInfoEntry = () => {
    setAdminInfoEntries((prev) => [...prev, { key: '', value: '' }]);
    setTimeout(() => adminInfoKeyInputRef.current?.focus(), 0);
  };

  const handleRemoveAdminInfoEntry = (index: number) => {
    setAdminInfoEntries(adminInfoEntries.filter((_, i) => i !== index));
  };

  const handleAdminInfoEntryChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...adminInfoEntries];
    updated[index] = { ...updated[index], [field]: value };
    setAdminInfoEntries(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const adminInfo: Record<string, any> = {};
      adminInfoEntries.forEach((entry) => {
        if (entry.key.trim()) {
          adminInfo[entry.key.trim()] = entry.value;
        }
      });

      const submitData: CreateGameProviderRequest = {
        ...form,
        adminInfo: Object.keys(adminInfo).length > 0 ? adminInfo : undefined,
        adminAccountLogin:
          form.adminAccountLogin?.username || form.adminAccountLogin?.password || form.adminAccountLogin?.note
            ? form.adminAccountLogin
            : undefined,
        adminLink: form.adminLink?.trim() || undefined,
        gameUrl: form.gameUrl?.trim() || undefined,
        displayOrder:
          form.displayOrder !== undefined && form.displayOrder !== null && !Number.isNaN(Number(form.displayOrder))
            ? Number(form.displayOrder)
            : undefined,
        clientTag:
          form.clientTag != null && String(form.clientTag).trim() !== '' ? String(form.clientTag).trim() : undefined,
      };

      await createProvider.mutateAsync(submitData);
      setForm({
        code: '',
        name: '',
        currency: 'USD',
        minDeposit: undefined,
        maxDeposit: undefined,
        status: 'active',
        displayOrder: undefined,
        clientTag: '',
        config: {},
        metadata: {},
        adminLink: '',
        gameUrl: '',
        adminAccountLogin: {
          username: '',
          password: '',
          note: '',
        },
        adminInfo: {},
      });
      setAdminInfoEntries([]);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Create provider error:', error);
    }
  };

  const currencySuffix = (form.currency || 'USD').trim() || '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4">
      <Card className="flex w-full max-w-2xl max-h-[90vh] flex-col overflow-hidden shadow-lg">
        <CardHeader className="flex-shrink-0 flex-row items-center justify-between space-y-0 border-b bg-card pb-4">
          <CardTitle className="text-lg font-semibold">Create Game Provider</CardTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </CardHeader>

        <CardContent className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 pt-4">
          <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
            <section className="rounded-lg border border-border/60 bg-muted/30 p-4">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Provider details</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-code">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="gp-create-code"
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g. provider1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="gp-create-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Provider display name"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-currency">
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="gp-create-currency"
                    list={CURRENCY_LIST_ID}
                    required
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    onBlur={(e) => {
                      const v = e.target.value.trim().toUpperCase();
                      if (v !== form.currency) setForm({ ...form, currency: v || 'USD' });
                    }}
                    placeholder="USD"
                    maxLength={12}
                    autoComplete="off"
                  />
                  <datalist id={CURRENCY_LIST_ID}>
                    {GAME_PROVIDER_CURRENCY_OPTIONS.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Pick from suggestions or type any ISO code (e.g. VND).
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-status">
                    Status
                  </label>
                  <select
                    id="gp-create-status"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className={gameProviderNativeSelectClass}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-min-dep">
                    Min deposit
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <Input
                        id="gp-create-min-dep"
                        type="number"
                        step="0.01"
                        value={form.minDeposit ?? ''}
                        onChange={(e) =>
                          setForm({ ...form, minDeposit: e.target.value ? parseFloat(e.target.value) : undefined })
                        }
                        placeholder="1.00"
                      />
                    </div>
                    <span
                      className="shrink-0 rounded-md border border-input bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground tabular-nums"
                      title="Matches selected currency"
                    >
                      {currencySuffix}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-max-dep">
                    Max deposit
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <Input
                        id="gp-create-max-dep"
                        type="number"
                        step="0.01"
                        value={form.maxDeposit ?? ''}
                        onChange={(e) =>
                          setForm({ ...form, maxDeposit: e.target.value ? parseFloat(e.target.value) : undefined })
                        }
                        placeholder="10000.00"
                      />
                    </div>
                    <span
                      className="shrink-0 rounded-md border border-input bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground tabular-nums"
                      title="Matches selected currency"
                    >
                      {currencySuffix}
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-display-order">
                    Display order <span className="font-normal text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    id="gp-create-display-order"
                    type="number"
                    value={form.displayOrder !== undefined && form.displayOrder !== null ? String(form.displayOrder) : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm({
                        ...form,
                        displayOrder: v === '' ? undefined : parseInt(v, 10),
                      });
                    }}
                    placeholder="e.g. 10"
                    aria-describedby="gp-create-display-order-help"
                  />
                  <p id="gp-create-display-order-help" className="mt-1 text-xs text-muted-foreground">
                    Lower numbers appear first. Leave empty for default (1000, end of list).
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-client-tag">
                    Client tag <span className="font-normal text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    id="gp-create-client-tag"
                    value={typeof form.clientTag === 'string' ? form.clientTag : ''}
                    onChange={(e) => setForm({ ...form, clientTag: e.target.value })}
                    placeholder="e.g. HOT"
                    maxLength={64}
                    aria-describedby="gp-create-client-tag-help"
                  />
                  <p id="gp-create-client-tag-help" className="mt-1 text-xs text-muted-foreground">
                    Badge label on kiosk cards; leave empty to hide the badge.
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-border/80 bg-slate-50/90 p-4 dark:bg-slate-950/40">
              <h3 className="mb-1 text-sm font-semibold text-foreground">Admin management</h3>
              <p className="mb-4 text-xs text-muted-foreground">
                Admin portal URLs and credentials for this provider.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-admin-link">
                    Admin link
                  </label>
                  <Input
                    id="gp-create-admin-link"
                    type="url"
                    value={form.adminLink || ''}
                    onChange={(e) => setForm({ ...form, adminLink: e.target.value })}
                    placeholder="https://admin.example.com"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="gp-create-game-url">
                    Game URL
                  </label>
                  <Input
                    id="gp-create-game-url"
                    type="url"
                    value={form.gameUrl || ''}
                    onChange={(e) => setForm({ ...form, gameUrl: e.target.value })}
                    placeholder="https://game.example.com"
                  />
                </div>

                <div className="rounded-lg border bg-background/80 p-4 shadow-sm">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Admin account login</label>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Internal use only. Follow your security policy when storing passwords.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs text-gray-600" htmlFor="gp-create-admin-user">
                        Username
                      </label>
                      <Input
                        id="gp-create-admin-user"
                        value={form.adminAccountLogin?.username || ''}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            adminAccountLogin: {
                              ...form.adminAccountLogin,
                              username: e.target.value,
                            },
                          })
                        }
                        placeholder="admin_user"
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-600" htmlFor="gp-create-admin-pass">
                        Password
                      </label>
                      <div className="relative flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            id="gp-create-admin-pass"
                            type={showPassword ? 'text' : 'password'}
                            value={form.adminAccountLogin?.password || ''}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                adminAccountLogin: {
                                  ...form.adminAccountLogin,
                                  password: e.target.value,
                                },
                              })
                            }
                            placeholder="••••••••"
                            className="pr-10"
                            autoComplete="new-password"
                            data-lpignore="true"
                            data-form-type="other"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 hover:text-gray-700"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {form.adminAccountLogin?.password ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const password = form.adminAccountLogin?.password || '';
                              if (password) {
                                const success = await copyToClipboard(password);
                                if (success) {
                                  toast.success('Password copied to clipboard');
                                } else {
                                  toast.error('Failed to copy password');
                                }
                              }
                            }}
                            className="flex-shrink-0"
                            aria-label="Copy password"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-600" htmlFor="gp-create-admin-note">
                        Note
                      </label>
                      <textarea
                        id="gp-create-admin-note"
                        value={form.adminAccountLogin?.note || ''}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            adminAccountLogin: {
                              ...form.adminAccountLogin,
                              note: e.target.value,
                            },
                          })
                        }
                        placeholder="Login notes, 2FA hints, etc."
                        rows={3}
                        className={cn(gameProviderTextareaClass)}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border bg-background/80 p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <label className="text-sm font-medium text-gray-700">Additional admin info</label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddAdminInfoEntry}>
                      <Plus className="mr-1 h-4 w-4" />
                      Add field
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {adminInfoEntries.map((entry, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          ref={index === adminInfoEntries.length - 1 ? adminInfoKeyInputRef : undefined}
                          placeholder="Field name"
                          value={entry.key}
                          onChange={(e) => handleAdminInfoEntryChange(index, 'key', e.target.value)}
                          className="flex-1"
                          aria-label={`Custom field name ${index + 1}`}
                        />
                        <Input
                          placeholder="Value"
                          value={entry.value}
                          onChange={(e) => handleAdminInfoEntryChange(index, 'value', e.target.value)}
                          className="flex-1"
                          aria-label={`Custom field value ${index + 1}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveAdminInfoEntry(index)}
                          aria-label={`Remove field ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {adminInfoEntries.length === 0 ? (
                      <p className="px-2 py-4 text-center text-xs text-muted-foreground">
                        No custom fields yet. Use <span className="font-medium text-foreground">Add field</span> for
                        IP allowlists, backup contacts, etc.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>
          </form>
        </CardContent>

        <CardFooter className="mt-0 flex flex-shrink-0 justify-end gap-3 border-t bg-card/95 py-4 supports-[backdrop-filter]:backdrop-blur-sm">
          <Button type="button" variant="outline" onClick={onClose} disabled={createProvider.isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={createProvider.isLoading}
            className="flex items-center space-x-2"
          >
            {createProvider.isLoading ? (
              <span>Creating…</span>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                <span>Create provider</span>
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CreateGameProviderModal;
